// MCHD Room Scheduler - Attempt Detail Modal Component
// Comprehensive modal for displaying failed login attempt details

import {
  Modal,
  Stack,
  Group,
  Text,
  Card,
  Divider,
  Grid,
  Alert,
  Button,
  Code,
  Timeline,
  Chip,
  Tooltip,
  CopyButton,
} from '@mantine/core';
import {
  IconShield,
  IconAlertCircle,
  IconCheck,
  IconLockOpen,
  IconBan,
  IconCopy,
} from '@tabler/icons-react';
import { FailedAttempt } from '../../api/endpoints/auth';
import DynamicRefreshButton from '../atoms/DynamicRefreshButton';

interface AttemptDetailModalProps {
  attempt: FailedAttempt | null;
  opened: boolean;
  onClose: () => void;
  onUnlockAccount: (email: string) => void;
  onUnlockIP: (ipAddress: string) => void;
  onRefresh: () => void;
  isUnlockingAccount?: boolean;
  isUnlockingIP?: boolean;
}

/**
 * Comprehensive modal for displaying failed login attempt details
 * @param props - Component props
 * @returns JSX element
 */
export function AttemptDetailModal({
  attempt,
  opened,
  onClose,
  onUnlockAccount,
  onUnlockIP,
  onRefresh,
  isUnlockingAccount = false,
  isUnlockingIP = false,
}: AttemptDetailModalProps): JSX.Element {
  if (!attempt) return <></>;

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const parseUserAgent = (userAgent: string) => {
    // Enhanced user agent parsing
    const info = {
      browser: 'Unknown',
      os: 'Unknown',
      device: 'Unknown',
      icon: '❓',
    };

    if (userAgent.includes('Chrome')) {
      info.browser = 'Chrome';
      info.icon = '🌐';
    } else if (userAgent.includes('Firefox')) {
      info.browser = 'Firefox';
      info.icon = '🦊';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      info.browser = 'Safari';
      info.icon = '🧭';
    } else if (userAgent.includes('Edge')) {
      info.browser = 'Edge';
      info.icon = '🌐';
    } else if (userAgent.includes('curl')) {
      info.browser = 'cURL';
      info.icon = '📡';
    } else if (userAgent.includes('Postman')) {
      info.browser = 'Postman';
      info.icon = '📮';
    }

    if (userAgent.includes('Windows')) info.os = 'Windows';
    else if (userAgent.includes('Mac OS')) info.os = 'macOS';
    else if (userAgent.includes('Linux')) info.os = 'Linux';
    else if (userAgent.includes('Android')) info.os = 'Android';
    else if (userAgent.includes('iOS')) info.os = 'iOS';

    if (userAgent.includes('Mobile')) info.device = 'Mobile';
    else if (userAgent.includes('Tablet')) info.device = 'Tablet';
    else info.device = 'Desktop';

    return info;
  };

  const getSeverityInfo = (attempt: FailedAttempt) => {
    const attemptedAt = new Date(attempt.attemptedAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - attemptedAt.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 30) return { severity: 'Critical', color: 'red', description: 'Very recent attempt' };
    if (diffInMinutes < 120) return { severity: 'High', color: 'orange', description: 'Recent attempt' };
    if (diffInMinutes < 1440) return { severity: 'Medium', color: 'yellow', description: 'Within 24 hours' };
    return { severity: 'Low', color: 'blue', description: 'Older attempt' };
  };

  const userAgentInfo = parseUserAgent(attempt.userAgent || '');
  const severityInfo = getSeverityInfo(attempt);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconShield size={20} color="var(--mantine-color-red-6)" />
          <Text fw={600}>Failed Login Attempt Details</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Status Alert */}
        <Alert
          icon={attempt.isResolved ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
          title={attempt.isResolved ? 'Resolved' : 'Unresolved'}
          color={attempt.isResolved ? 'green' : 'red'}
          variant="light"
        >
          {attempt.isResolved 
            ? 'This attempt has been resolved and is no longer blocking access.'
            : 'This attempt is still active and may be blocking access to the account.'
          }
        </Alert>

        {/* Basic Information */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">Basic Information</Text>
          
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Email Address</Text>
                <Group gap="xs">
                  <Text size="sm" ff="monospace">{attempt.email}</Text>
                  <CopyButton value={attempt.email}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied!' : 'Copy email'}>
                        <Button
                          variant="subtle"
                          size="xs"
                          color={copied ? 'green' : 'gray'}
                          onClick={copy}
                        >
                          <IconCopy size={12} />
                        </Button>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">IP Address</Text>
                <Group gap="xs">
                  <Text size="sm" ff="monospace">{attempt.ipAddress}</Text>
                  <CopyButton value={attempt.ipAddress}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied!' : 'Copy IP'}>
                        <Button
                          variant="subtle"
                          size="xs"
                          color={copied ? 'green' : 'gray'}
                          onClick={copy}
                        >
                          <IconCopy size={12} />
                        </Button>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Attempt Details */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">Attempt Details</Text>
          
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Severity Level</Text>
                <Chip
                  color={severityInfo.color}
                  variant="light"
                  size="sm"
                >
                  {severityInfo.severity}
                </Chip>
                <Text size="xs" c="dimmed">{severityInfo.description}</Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Attempt Time</Text>
                <Text size="sm">{getTimeAgo(attempt.attemptedAt)}</Text>
                <Text size="xs" c="dimmed">
                  {new Date(attempt.attemptedAt).toLocaleString()}
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>

        {/* User Agent Information */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">Client Information</Text>
          
          <Grid>
            <Grid.Col span={4}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Browser</Text>
                <Group gap="xs">
                  <Text size="sm">{userAgentInfo.icon}</Text>
                  <Text size="sm">{userAgentInfo.browser}</Text>
                </Group>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Operating System</Text>
                <Text size="sm">{userAgentInfo.os}</Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Stack gap="xs">
                <Text size="sm" fw={500} c="dimmed">Device Type</Text>
                <Text size="sm">{userAgentInfo.device}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
          
          <Divider my="md" />
          
          <Stack gap="xs">
            <Text size="sm" fw={500} c="dimmed">Full User Agent String</Text>
            <Code block style={{ fontSize: '12px', maxHeight: '100px', overflow: 'auto' }}>
              {attempt.userAgent}
            </Code>
          </Stack>
        </Card>

        {/* Resolution Information */}
        {attempt.isResolved && (
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Text size="lg" fw={600} mb="md">Resolution Details</Text>
            
            <Timeline active={1} bulletSize={24} lineWidth={2}>
              <Timeline.Item
                bullet={<IconAlertCircle size={12} />}
                title="Failed Attempt"
                color="red"
              >
                <Text size="sm" c="dimmed">
                  {new Date(attempt.attemptedAt).toLocaleString()}
                </Text>
              </Timeline.Item>
              
              <Timeline.Item
                bullet={<IconCheck size={12} />}
                title="Resolved"
                color="green"
              >
                <Text size="sm" c="dimmed">
                  {attempt.resolvedAt && new Date(attempt.resolvedAt).toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Resolved by: {attempt.resolvedBy === 'system' ? 'System' : attempt.resolvedBy}
                </Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        )}

        {/* Actions */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">Actions</Text>
          
          <Group gap="sm">
            <DynamicRefreshButton
              variant="outline"
              onRefresh={onRefresh}
              size="sm"
              color="blue"
              children="Refresh Data"
            />
            
            {!attempt.isResolved && (
              <>
                <Button
                  color="green"
                  leftSection={<IconLockOpen size={16} />}
                  onClick={() => onUnlockAccount(attempt.email)}
                  loading={isUnlockingAccount}
                >
                  Unlock Account
                </Button>
                
                <Button
                  color="orange"
                  leftSection={<IconBan size={16} />}
                  onClick={() => onUnlockIP(attempt.ipAddress)}
                  loading={isUnlockingIP}
                >
                  Unlock IP Address
                </Button>
              </>
            )}
          </Group>
        </Card>

        {/* Footer Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
